import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';

import FirmwareCard from './firmware-card.jsx';
import ElioDFU, {ElioDFUError, ElioDFUPhase} from '../../lib/elio-dfu/ElioDFU.js';
import {loadManifest, loadFirmwareZip} from '../../lib/elio-dfu/firmware-catalog.js';

import styles from './firmware-tab.css';

const phaseMessages = {
    [ElioDFUPhase.IDLE]: {
        id: 'gui.firmwareTab.phase.idle',
        defaultMessage: 'Ready'
    },
    [ElioDFUPhase.LOADING_PACKAGE]: {
        id: 'gui.firmwareTab.phase.loading',
        defaultMessage: 'Loading firmware package...'
    },
    [ElioDFUPhase.SELECTING_DEVICE]: {
        id: 'gui.firmwareTab.phase.selecting',
        defaultMessage: 'Step 1/3 — Pick your device (named ELIO_xxxx) in the browser dialog and click 페어링.'
    },
    [ElioDFUPhase.ENTERING_BOOTLOADER]: {
        id: 'gui.firmwareTab.phase.entering',
        defaultMessage: 'Step 2/3 — Sending bootloader command. Device will reboot in a moment...'
    },
    [ElioDFUPhase.AWAITING_RECONNECT]: {
        id: 'gui.firmwareTab.phase.awaitingReconnect',
        defaultMessage: 'Device rebooted into bootloader. Click Continue, then pick "ELIO_DFU…" in the picker.'
    },
    [ElioDFUPhase.RECONNECTING]: {
        id: 'gui.firmwareTab.phase.reconnecting',
        defaultMessage: 'Step 3/3 — Pick the bootloader device (ELIO_DFU…) and click 페어링.'
    },
    [ElioDFUPhase.TRANSFERRING_INIT]: {
        id: 'gui.firmwareTab.phase.transferringInit',
        defaultMessage: 'Sending init packet...'
    },
    [ElioDFUPhase.TRANSFERRING_FIRMWARE]: {
        id: 'gui.firmwareTab.phase.transferringFirmware',
        defaultMessage: 'Transferring firmware...'
    },
    [ElioDFUPhase.COMPLETED]: {
        id: 'gui.firmwareTab.phase.completed',
        defaultMessage: 'Update completed!'
    },
    [ElioDFUPhase.FAILED]: {
        id: 'gui.firmwareTab.phase.failed',
        defaultMessage: 'Update failed'
    }
};

const errorMessages = {
    [ElioDFUError.BROWSER_UNSUPPORTED]: {
        id: 'gui.firmwareTab.error.browserUnsupported',
        defaultMessage: 'Web Bluetooth is not supported in this browser. Use Chrome or Edge on desktop or Android.'
    },
    [ElioDFUError.BLUETOOTH_DISABLED]: {
        id: 'gui.firmwareTab.error.bluetoothDisabled',
        defaultMessage: 'Bluetooth is disabled. Please enable it in your OS settings.'
    },
    [ElioDFUError.USER_CANCELLED]: {
        id: 'gui.firmwareTab.error.userCancelled',
        defaultMessage: 'Cancelled by user.'
    },
    [ElioDFUError.DEVICE_DISCONNECTED]: {
        id: 'gui.firmwareTab.error.deviceDisconnected',
        defaultMessage: 'The device disconnected during update. Please retry.'
    },
    [ElioDFUError.DFU_VERIFY_FAILED]: {
        id: 'gui.firmwareTab.error.verifyFailed',
        defaultMessage: 'Firmware verification failed. The previous firmware is preserved on the device.'
    },
    [ElioDFUError.ZIP_INVALID]: {
        id: 'gui.firmwareTab.error.zipInvalid',
        defaultMessage: 'The firmware package is invalid.'
    },
    [ElioDFUError.UNKNOWN]: {
        id: 'gui.firmwareTab.error.unknown',
        defaultMessage: 'An unexpected error occurred.'
    }
};

class FirmwareTab extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            supported: ElioDFU.isSupported(),
            firmwares: [],
            loadError: null,
            activeFirmware: null,
            phase: ElioDFUPhase.IDLE,
            progressPercent: 0,
            progressBytes: 0,
            totalBytes: 0,
            errorCode: null,
            errorMessage: null,
            success: false
        };
        this._dfu = null;
        this.handleUpdate = this.handleUpdate.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleContinue = this.handleContinue.bind(this);
    }

    handleContinue () {
        if (!this._dfu) return;
        this.setState({errorCode: null, errorMessage: null});
        this._dfu.continueUpdate().catch(err => {
            if (typeof console !== 'undefined') console.error('[FirmwareTab] continue', err);
        });
    }

    componentDidMount () {
        loadManifest()
            .then(manifest => this.setState({firmwares: manifest.firmwares || []}))
            .catch(err => this.setState({loadError: err.message}));
    }

    handleUpdate (firmware) {
        this.setState({
            activeFirmware: firmware,
            phase: ElioDFUPhase.LOADING_PACKAGE,
            progressPercent: 0,
            progressBytes: 0,
            totalBytes: 0,
            errorCode: null,
            errorMessage: null,
            success: false
        });

        const dfu = new ElioDFU();
        this._dfu = dfu;
        dfu.addEventListener('phase', evt => {
            this.setState({phase: evt.detail.phase});
        });
        dfu.addEventListener('progress', evt => {
            this.setState({
                progressPercent: evt.detail.percent,
                progressBytes: evt.detail.currentBytes,
                totalBytes: evt.detail.totalBytes
            });
        });
        dfu.addEventListener('error', evt => {
            // User-cancelled isn't really an error — just close the modal.
            if (evt.detail.code === ElioDFUError.USER_CANCELLED) {
                this.handleClose();
                return;
            }
            this.setState({
                errorCode: evt.detail.code,
                errorMessage: evt.detail.message
            });
        });
        dfu.addEventListener('completed', () => {
            this.setState({success: true});
        });

        loadFirmwareZip(firmware.file)
            .then(buf => dfu.update(buf))
            .catch(err => {
                // already surfaced via error event for known cases; ignore re-throw
                if (typeof console !== 'undefined') console.error('[FirmwareTab]', err);
            });
    }

    handleClose () {
        this.setState({
            activeFirmware: null,
            phase: ElioDFUPhase.IDLE,
            progressPercent: 0,
            errorCode: null,
            errorMessage: null,
            success: false
        });
        this._dfu = null;
    }

    render () {
        const {intl} = this.props;
        const locale = (intl.locale || 'en').split('-')[0];
        const {
            supported, firmwares, loadError, activeFirmware,
            phase, progressPercent, progressBytes, totalBytes,
            errorCode, success
        } = this.state;

        const inProgress = activeFirmware !== null;
        const finished = success || phase === ElioDFUPhase.FAILED;

        if (!supported) {
            return (
                <div className={styles.firmwareTab}>
                    <div className={styles.unsupported}>
                        <h2>
                            <FormattedMessage
                                defaultMessage="Web Bluetooth is not supported"
                                description="Header shown when Web Bluetooth API is unavailable"
                                id="gui.firmwareTab.unsupported.title"
                            />
                        </h2>
                        <p>
                            <FormattedMessage
                                defaultMessage="Please use Chrome or Edge on Windows, macOS, Linux, or Android to update ELIO firmware. iOS Safari is not supported."
                                description="Help text for unsupported browsers"
                                id="gui.firmwareTab.unsupported.body"
                            />
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.firmwareTab}>
                <div className={styles.header}>
                    <h1 className={styles.title}>
                        <FormattedMessage
                            defaultMessage="ELIO Firmware Update"
                            description="Title of the firmware update tab"
                            id="gui.firmwareTab.title"
                        />
                    </h1>
                    <p className={styles.subtitle}>
                        <FormattedMessage
                            defaultMessage="Pick a firmware below. Make sure your ELIO is powered on and within Bluetooth range."
                            description="Subtitle for firmware tab"
                            id="gui.firmwareTab.subtitle"
                        />
                    </p>
                </div>

                {loadError ? (
                    <div className={styles.errorMessage}>{loadError}</div>
                ) : (
                    <div className={styles.grid}>
                        {firmwares.map(fw => (
                            <FirmwareCard
                                key={fw.id}
                                firmware={fw}
                                locale={locale}
                                disabled={inProgress}
                                onUpdate={this.handleUpdate}
                            />
                        ))}
                    </div>
                )}

                {inProgress ? (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modal}>
                            <h2 className={styles.modalTitle}>
                                {(activeFirmware.name && activeFirmware.name[locale]) ||
                                    activeFirmware.name.en || activeFirmware.id}
                            </h2>

                            <div className={styles.modalWarning}>
                                {phase === ElioDFUPhase.SELECTING_DEVICE ? (
                                    <FormattedMessage
                                        defaultMessage="👉 In the picker, select your device named ELIO_xxxx (e.g. ELIO_A64B) and click 페어링. The device will reboot — that is expected."
                                        description="Hint for first picker"
                                        id="gui.firmwareTab.hint.firstPick"
                                    />
                                ) : phase === ElioDFUPhase.AWAITING_RECONNECT ? (
                                    <FormattedMessage
                                        defaultMessage="✅ Device is now in bootloader mode. Click Continue, then in the picker select ELIO_DFU_xxxx and click 페어링."
                                        description="Hint between buttonless and second pick"
                                        id="gui.firmwareTab.hint.continue"
                                    />
                                ) : phase === ElioDFUPhase.RECONNECTING ? (
                                    <FormattedMessage
                                        defaultMessage="👉 In the picker, select ELIO_DFU_xxxx and click 페어링. Firmware will then transfer."
                                        description="Hint for second picker"
                                        id="gui.firmwareTab.hint.secondPick"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="During update the device briefly reboots into bootloader (ELIO_DFU…). Keep this tab open until the update completes."
                                        description="Generic warning during DFU"
                                        id="gui.firmwareTab.dfuTargWarning"
                                    />
                                )}
                            </div>

                            <div className={styles.modalPhase}>
                                {phaseMessages[phase] ? (
                                    <FormattedMessage {...phaseMessages[phase]} />
                                ) : phase}
                            </div>

                            <div className={styles.progressTrack}>
                                <div
                                    className={styles.progressBar}
                                    style={{width: `${progressPercent}%`}}
                                />
                            </div>
                            <div className={styles.progressText}>
                                {totalBytes > 0 ?
                                    `${progressBytes.toLocaleString()} / ${totalBytes.toLocaleString()} B (${progressPercent}%)` :
                                    `${progressPercent}%`}
                            </div>

                            {success ? (
                                <div className={styles.successMessage}>
                                    <FormattedMessage
                                        defaultMessage="Firmware updated successfully!"
                                        description="Success message"
                                        id="gui.firmwareTab.success"
                                    />
                                </div>
                            ) : null}

                            {errorCode ? (
                                <div className={styles.errorMessage}>
                                    {errorMessages[errorCode] ? (
                                        <FormattedMessage {...errorMessages[errorCode]} />
                                    ) : this.state.errorMessage}
                                </div>
                            ) : null}

                            <div className={styles.modalActions}>
                                {phase === ElioDFUPhase.AWAITING_RECONNECT ? (
                                    <button
                                        className={styles.btnPrimary}
                                        onClick={this.handleContinue}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Continue"
                                            description="Button to continue DFU after device reboots into bootloader"
                                            id="gui.firmwareTab.continue"
                                        />
                                    </button>
                                ) : null}
                                <button
                                    className={finished ? styles.btnPrimary : styles.btnCancel}
                                    onClick={this.handleClose}
                                >
                                    {finished ? (
                                        <FormattedMessage
                                            defaultMessage="Close"
                                            description="Close button"
                                            id="gui.firmwareTab.close"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Cancel"
                                            description="Cancel button"
                                            id="gui.firmwareTab.cancel"
                                        />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

FirmwareTab.propTypes = {
    intl: intlShape
};

export default injectIntl(FirmwareTab);
